import * as React from 'react';
import { FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * View / replace a SINGLE domain's Apple domain-association file.
 *
 * Apple issues a distinct association file per merchant domain (it embeds the
 * domain), so every domain stores and serves its own file — they are not shared
 * across the registration. Merchant and account-manager surfaces pass their own
 * query/mutation hooks so this dialog stays surface-agnostic.
 */
type FileQueryHook = (
  domainId: string,
  opts?: { enabled?: boolean },
) => { data?: { associationFileContent: string | null; isDomainSpecific?: boolean }; isLoading: boolean };

type UpdateHook = () => {
  mutate: (
    input: { domainId: string; associationFileContent: string },
    opts?: { onSuccess?: () => void },
  ) => void;
  isPending: boolean;
};

export function DomainAssociationFileDialog({
  domainId,
  domain,
  open,
  onOpenChange,
  useFileQuery,
  useUpdateFile,
}: {
  domainId: string;
  domain: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  useFileQuery: FileQueryHook;
  useUpdateFile: UpdateHook;
}) {
  const query = useFileQuery(domainId, { enabled: open });
  const update = useUpdateFile();
  const ref = React.useRef<HTMLTextAreaElement>(null);

  const current = query.data?.associationFileContent ?? '';
  const inherited = query.data && !query.data.isDomainSpecific && !!query.data.associationFileContent;

  const handleSave = () => {
    const value = ref.current?.value.trim() ?? '';
    if (!value) return;
    update.mutate({ domainId, associationFileContent: value }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Association file
          </DialogTitle>
          <DialogDescription>
            Apple gives a different{' '}
            <span className="font-mono">apple-developer-merchantid-domain-association</span> file for each
            domain. Paste the file for <span className="font-mono">{domain}</span> — we serve it at its{' '}
            <span className="font-mono">/.well-known/</span> path. Verify the domain in Apple, then Probe it.
          </DialogDescription>
        </DialogHeader>

        {inherited && (
          <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            This domain is currently serving the registration&rsquo;s shared file. Paste this domain&rsquo;s
            own file from Apple to replace it.
          </p>
        )}

        {query.isLoading ? (
          <div className="text-muted-foreground py-8 text-center text-sm">Loading…</div>
        ) : (
          <Textarea
            ref={ref}
            key={current || 'empty'}
            defaultValue={current}
            placeholder="Paste the base64 association file contents…"
            className="min-h-[160px] font-mono text-xs"
            autoFocus
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={update.isPending || query.isLoading}>
            {update.isPending ? 'Saving…' : 'Save file'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
